import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Reveal } from "@/components/motion/Reveal";
import { Quote } from "lucide-react";

type CaseStudy = {
  id: string;
  vertical_slug: string | null;
  company_name: string;
  display_name_override: string | null;
  company_logo_url: string | null;
  persona_title: string | null;
  headline: string;
  summary: string | null;
  metric_meetings_booked: number | null;
  metric_reply_rate: number | null;
  metric_pipeline_value_cents: number | null;
  metric_close_count: number | null;
  quote: string | null;
  quote_author: string | null;
  quote_author_title: string | null;
  consent_status: string;
};

interface Props {
  vertical?: string | null;
}

/**
 * Real, source-attributed case studies. No theatre, no setInterval.
 * Empty state shows honest live counters from the DB.
 */
export function RealProof({ vertical }: Props) {
  const { data: studies } = useQuery({
    queryKey: ["case-studies-public", vertical ?? "all"],
    queryFn: async () => {
      let q = supabase
        .from("case_studies")
        .select(
          "id, vertical_slug, company_name, display_name_override, company_logo_url, persona_title, headline, summary, metric_meetings_booked, metric_reply_rate, metric_pipeline_value_cents, metric_close_count, quote, quote_author, quote_author_title, consent_status"
        )
        .eq("published", true)
        .order("featured", { ascending: false })
        .order("display_order", { ascending: true })
        .limit(3);
      if (vertical) q = q.eq("vertical_slug", vertical);
      const { data } = await q;
      return (data ?? []) as CaseStudy[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: liveCounts } = useQuery({
    queryKey: ["public-honest-counts"],
    queryFn: async () => {
      const [tenants, sent, meetings] = await Promise.all([
        supabase.from("tenants").select("id", { count: "exact", head: true }),
        supabase.from("sdr_emails").select("id", { count: "exact", head: true }).eq("direction", "outbound"),
        supabase.from("bookings").select("id", { count: "exact", head: true }).in("event_status", ["confirmed", "completed"]),
      ]);
      return {
        tenants: tenants.count ?? 0,
        sent: sent.count ?? 0,
        meetings: meetings.count ?? 0,
      };
    },
    staleTime: 5 * 60 * 1000,
    enabled: !studies || studies.length === 0,
  });

  const hasStudies = (studies?.length ?? 0) > 0;

  return (
    <section className="relative">
      <div className="max-w-5xl mx-auto px-4 py-20 md:py-28">
        <Reveal stagger={60} className="text-center space-y-3 max-w-2xl mx-auto mb-12">
          <p className="text-[10px] font-mono uppercase tracking-widest text-primary">
            Real proof
          </p>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground tracking-tight">
            {hasStudies ? "Customers who replaced an SDR" : "Building proof in public"}
          </h2>
          <p className="text-sm md:text-base text-foreground/75 max-w-xl mx-auto">
            {hasStudies
              ? "Real names, real metrics, real quotes. Every story below is sourced from a customer who agreed to be named or anonymized."
              : "We don't fake numbers. Real, named case studies will appear here as we earn them. Until then, here's exactly where we stand."}
          </p>
        </Reveal>

        {hasStudies ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {studies!.map((s) => (
              <CaseStudyCard key={s.id} study={s} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3 md:gap-5 max-w-2xl mx-auto">
            <Stat label="Active workspaces" value={liveCounts?.tenants ?? 0} />
            <Stat label="Outbound emails sent" value={liveCounts?.sent ?? 0} />
            <Stat label="Meetings booked" value={liveCounts?.meetings ?? 0} />
          </div>
        )}

        {!hasStudies && (
          <p className="text-center text-xs text-foreground/55 mt-8 max-w-md mx-auto">
            Counts above are queried live from our database. Refresh this page — they will not invent themselves.
          </p>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border/50 bg-card/40 backdrop-blur-sm p-4 md:p-6 text-center">
      <div className="text-2xl md:text-3xl font-bold text-foreground tabular-nums">
        {value.toLocaleString()}
      </div>
      <div className="text-[10px] md:text-xs font-mono uppercase tracking-wider text-foreground/55 mt-1">
        {label}
      </div>
    </div>
  );
}

function CaseStudyCard({ study }: { study: CaseStudy }) {
  const displayName = study.display_name_override || study.company_name;
  const isAnonymized = study.consent_status === "anonymized" || !!study.display_name_override;

  return (
    <div className="rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        {study.company_logo_url && !isAnonymized ? (
          <img src={study.company_logo_url} alt={displayName} className="h-7 max-w-[140px] object-contain" />
        ) : (
          <span className="text-sm font-semibold text-foreground">{displayName}</span>
        )}
        {isAnonymized && (
          <span className="text-[9px] font-mono uppercase tracking-wider text-foreground/55">Anonymized</span>
        )}
      </div>

      <h3 className="text-sm font-semibold text-foreground leading-snug">{study.headline}</h3>

      <div className="grid grid-cols-3 gap-2 mt-1">
        <Metric value={study.metric_meetings_booked ?? 0} label="Meetings" />
        <Metric value={`${study.metric_reply_rate ?? 0}%`} label="Reply rate" />
        <Metric value={study.metric_close_count ?? 0} label="Closed" />
      </div>

      {study.quote && (
        <blockquote className="text-xs text-foreground/75 italic border-l-2 border-primary/50 pl-3 mt-2">
          <Quote className="h-3 w-3 text-primary/60 mb-1" />
          {study.quote}
          {study.quote_author && (
            <footer className="not-italic text-[10px] text-foreground/55 mt-1">
              — {study.quote_author}
              {study.quote_author_title && `, ${study.quote_author_title}`}
            </footer>
          )}
        </blockquote>
      )}
    </div>
  );
}

function Metric({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="text-center">
      <div className="text-base font-bold text-foreground tabular-nums">{value}</div>
      <div className="text-[9px] font-mono uppercase tracking-wider text-foreground/55">{label}</div>
    </div>
  );
}
